// Bulk CSV import for the Brand · Customers page — background flow.
//
// Uploads the raw file to `POST /api/customers/import` (multipart) so
// even a 35 000-row CSV never blocks the request. The backend
// immediately returns `{status: "processing", job_id, total}` and does
// the heavy work in a BackgroundTask that batches inserts 100 at a
// time.
//
// We then poll `GET /api/customers/import/jobs/{job_id}` every 2 s to
// stream progress into the dialog ("Importing… 5,000 / 35,000
// complete") until the job flips to `completed` or `failed`.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { staffApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle2, AlertTriangle, XCircle, FileSpreadsheet, Loader2, GitMerge } from "lucide-react";

const SAMPLE_CSV = [
  "First Name,Last Name,Email,Phone/WhatsApp,Nationality,DOB,Gender",
  "Wayan,Sari,wayan.sari@example.com,+6281234567890,Indonesian,1992-04-18,Female",
  "James,Miller,james.miller@example.com,+61412000123,Australian,1985-11-02,Male",
].join("\n") + "\n";

const POLL_MS = 2000;
// Polls tolerate transient Cloudflare 520 / network blips (the deployed
// edge occasionally returns "could not parse" while the origin is happily
// grinding through the import). We keep polling silently until we've hit
// too many consecutive failures OR too long without any success.
const MAX_CONSECUTIVE_POLL_ERRORS = 8;      // ~40-80s of blank polls before we give up
const MAX_POLL_SILENCE_MS = 5 * 60 * 1000;  // hard cap: 5 minutes since the last good poll

export default function ImportCustomersDialog({ open, onOpenChange, onImported }) {
  const inputRef = useRef(null);
  const pollTimerRef = useRef(null);
  const toastIdRef = useRef(null);
  const reconnectToastRef = useRef(null);
  const pollErrorCountRef = useRef(0);
  const lastPollOkRef = useRef(0);
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | uploading | processing | done | failed
  const [job, setJob] = useState(null);
  const [headerError, setHeaderError] = useState(null);
  // Iter-374 — duplicate-review state. After an import completes, the
  // operator can tick which flagged duplicates should still be inserted
  // (as fresh profiles) for manual merge later.
  const [pickedRows, setPickedRows] = useState(() => new Set());
  const [forcing, setForcing] = useState(false);

  const reset = () => {
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    if (toastIdRef.current) { toast.dismiss(toastIdRef.current); toastIdRef.current = null; }
    if (reconnectToastRef.current) { toast.dismiss(reconnectToastRef.current); reconnectToastRef.current = null; }
    pollErrorCountRef.current = 0;
    lastPollOkRef.current = 0;
    setFileName("");
    setFile(null);
    setPhase("idle");
    setJob(null);
    setHeaderError(null);
    setPickedRows(new Set());
    setForcing(false);
  };

  const closeAndReset = () => { onOpenChange(false); setTimeout(reset, 200); };

  // Clean up any in-flight poll when the dialog unmounts.
  useEffect(() => () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    if (toastIdRef.current) toast.dismiss(toastIdRef.current);
    if (reconnectToastRef.current) toast.dismiss(reconnectToastRef.current);
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setFileName(f.name);
    setHeaderError(null);
    setJob(null);
    setPhase("idle");
  };

  const pollJob = async (jobId) => {
    try {
      const r = await staffApi.get(`/customers/import/jobs/${jobId}`);
      const j = r.data;
      // Success — reset the error counter and dismiss any "reconnecting" toast.
      pollErrorCountRef.current = 0;
      lastPollOkRef.current = Date.now();
      if (reconnectToastRef.current) { toast.dismiss(reconnectToastRef.current); reconnectToastRef.current = null; }
      setJob(j);
      // Refresh the persistent progress toast so the operator can see progress
      // even after they close the dialog.
      if (j.status === "processing") {
        toastIdRef.current = toast.loading(
          `Importing… ${(j.imported_count || 0).toLocaleString()} / ${(j.total || 0).toLocaleString()} complete`,
          { id: toastIdRef.current || undefined, duration: Infinity },
        );
        pollTimerRef.current = setTimeout(() => pollJob(jobId), POLL_MS);
      } else {
        // Terminal — flip UI, close toast.
        if (toastIdRef.current) { toast.dismiss(toastIdRef.current); toastIdRef.current = null; }
        setPhase(j.status === "completed" ? "done" : "failed");
        if (j.status === "completed") {
          toast.success(`Imported ${(j.imported_count || 0).toLocaleString()} customer${j.imported_count === 1 ? "" : "s"}.`);
          onImported && onImported(j);
        } else {
          toast.error(j.header_error || j.message || "Import failed.");
        }
      }
    } catch (err) {
      // Transient network / Cloudflare 520 / 502 / 504 during polling MUST
      // NOT terminate the flow — the background import on the server keeps
      // running. We retry with a mild backoff and only give up after too
      // many consecutive failures OR too long without any successful poll.
      pollErrorCountRef.current += 1;
      const consecutive = pollErrorCountRef.current;
      const silentFor = lastPollOkRef.current ? (Date.now() - lastPollOkRef.current) : 0;
      const giveUp = consecutive >= MAX_CONSECUTIVE_POLL_ERRORS || silentFor > MAX_POLL_SILENCE_MS;
      if (giveUp) {
        if (toastIdRef.current) { toast.dismiss(toastIdRef.current); toastIdRef.current = null; }
        if (reconnectToastRef.current) { toast.dismiss(reconnectToastRef.current); reconnectToastRef.current = null; }
        toast.error(
          `Lost connection while tracking the import — the background job may still be running. Refresh the page or check Brand → Customers in a moment. (${formatApiError(err)})`,
          { duration: 8000 },
        );
        setPhase("failed");
        return;
      }
      // Soft warn once we've missed 2 polls so the operator knows something
      // hiccuped without seeing a full error toast.
      if (consecutive >= 2) {
        reconnectToastRef.current = toast.loading(
          `Reconnecting… (attempt ${consecutive}/${MAX_CONSECUTIVE_POLL_ERRORS}). Import is still running in the background.`,
          { id: reconnectToastRef.current || undefined, duration: Infinity },
        );
      }
      // Exponential-ish backoff, capped at 8 s, so a flaky edge doesn't hammer.
      const delay = Math.min(POLL_MS * Math.pow(1.5, consecutive - 1), 8000);
      pollTimerRef.current = setTimeout(() => pollJob(jobId), delay);
    }
  };

  const doImport = async () => {
    if (!file) return;
    setPhase("uploading");
    setJob(null);
    setHeaderError(null);
    setPickedRows(new Set());
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      const r = await staffApi.post("/customers/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = r.data;
      if (!data.ok || data.status !== "processing") {
        setHeaderError(data.header_error || data.message || "Import could not start.");
        setPhase("failed");
        toast.error(data.header_error || data.message || "Import could not start.");
        return;
      }
      setPhase("processing");
      setJob({ ...data, imported_count: 0, duplicate_count: 0, error_count: 0, processed: 0, percent: 0 });
      toastIdRef.current = toast.loading(data.message, { duration: Infinity });
      // Reset the poll-resilience counters at the start of a fresh job.
      pollErrorCountRef.current = 0;
      lastPollOkRef.current = Date.now();
      // Kick off polling.
      pollJob(data.job_id);
    } catch (err) {
      toast.error(formatApiError(err));
      setPhase("idle");
    }
  };

  // Iter-374 — force-import the duplicates the operator ticked. The server
  // creates each one as a brand new Pending-Member profile flagged
  // `forced_duplicate=true` so the admin can run a manual merge later.
  const toggleRow = (row) => {
    setPickedRows((prev) => {
      const next = new Set(prev);
      if (next.has(row)) next.delete(row); else next.add(row);
      return next;
    });
  };
  const togglePickAll = (allRows, allOn) => {
    setPickedRows(() => (allOn ? new Set() : new Set(allRows)));
  };
  const forceImportSelected = async () => {
    if (!job?.id || pickedRows.size === 0) return;
    setForcing(true);
    try {
      const r = await staffApi.post(`/customers/import/jobs/${job.id}/force-import`, {
        row_indices: Array.from(pickedRows),
      });
      const data = r.data;
      toast.success(data.message || `Imported ${data.imported_count} duplicates.`);
      // Refresh the job doc so counts + per-row "forced_imported" flags update.
      const refreshed = await staffApi.get(`/customers/import/jobs/${job.id}`);
      setJob(refreshed.data);
      setPickedRows(new Set());
      onImported && onImported(refreshed.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setForcing(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cocoon-customers-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const percent = job?.percent != null ? Math.min(100, Math.max(0, Math.round(job.percent))) : (phase === "uploading" ? 15 : 0);
  const total = job?.total || 0;
  const imported = job?.imported_count || 0;
  const duplicates = job?.duplicate_count || 0;
  const errored = job?.error_count || 0;
  // Iter-374 — unforced dupes drive the checkbox list. Forced ones stay
  // visible as "✓ imported as new" so the operator sees what they did.
  const dupRows = useMemo(() => job?.duplicates_sample || [], [job]);
  const pickableRows = useMemo(() => dupRows.filter((d) => !d.forced_imported).map((d) => d.row), [dupRows]);
  const allPicked = pickableRows.length > 0 && pickableRows.every((r) => pickedRows.has(r));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeAndReset(); else onOpenChange(true); }}>
      <DialogContent className="max-w-2xl" data-testid="import-csv-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-primary" /> Import customers · CSV
          </DialogTitle>
          <DialogDescription>
            Upload a CSV with the headers <code className="text-[11px] bg-muted px-1 rounded">First Name, Last Name, Email, Phone/WhatsApp, Nationality, DOB, Gender</code>. Large files (10k+) run in the background — you can close this dialog and the toast will keep you posted.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-2">
          <div>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={onFile}
              data-testid="import-csv-file-input"
            />
            <Button
              variant="outline"
              onClick={() => inputRef.current?.click()}
              className="w-full h-11 gap-2 border-dashed"
              disabled={phase === "uploading" || phase === "processing"}
              data-testid="import-csv-pick-file"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {fileName ? `Selected: ${fileName}` : "Pick a CSV file…"}
            </Button>
          </div>

          {headerError && (
            <div className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2" data-testid="import-csv-header-error">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{headerError}</span>
            </div>
          )}

          {(phase === "uploading" || phase === "processing") && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span data-testid="import-csv-progress-label">
                  {phase === "uploading"
                    ? "Uploading file…"
                    : `Importing… ${imported.toLocaleString()} / ${total.toLocaleString()} complete`}
                </span>
              </div>
              <Progress value={percent} data-testid="import-csv-progress" />
              <p className="text-[11px] text-muted-foreground text-right">{percent}%</p>
            </div>
          )}

          {phase === "done" && job && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-2 text-xs" data-testid="import-csv-result">
              <div className="flex items-center gap-1.5 text-emerald-900 font-medium">
                <CheckCircle2 className="w-4 h-4" /> Import complete
              </div>
              <p>
                <span className="font-semibold text-foreground" data-testid="import-csv-imported-count">{imported.toLocaleString()}</span>
                {" "}customer{imported === 1 ? "" : "s"} imported as Pending Members.
              </p>
              {errored > 0 && (
                <p className="text-rose-800 flex items-center gap-1">
                  <XCircle className="w-3 h-3" />
                  <span className="font-semibold" data-testid="import-csv-error-count">{errored.toLocaleString()}</span>
                  {" "}row{errored === 1 ? "" : "s"} rejected (missing required fields).
                </p>
              )}

              {dupRows.length > 0 && (
                <div className="rounded border border-amber-200 bg-white/70 p-2 space-y-2" data-testid="import-csv-duplicates-panel">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-amber-900 font-medium flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span data-testid="import-csv-duplicate-count">{duplicates.toLocaleString()}</span>
                      {" "}duplicate{duplicates === 1 ? "" : "s"} skipped
                    </p>
                    <label className="flex items-center gap-1.5 text-amber-900 cursor-pointer select-none">
                      <Checkbox
                        checked={allPicked}
                        onCheckedChange={() => togglePickAll(pickableRows, allPicked)}
                        disabled={pickableRows.length === 0 || forcing}
                        data-testid="import-csv-dup-select-all"
                      />
                      <span>Select all</span>
                    </label>
                  </div>
                  <p className="text-[11px] text-amber-800">
                    Tick the ones you still want to import as a brand-new profile — a brand admin can merge them later.
                  </p>
                  <div className="max-h-56 overflow-auto rounded border border-amber-100 bg-white">
                    <table className="w-full text-[11px]">
                      <thead className="bg-amber-50 text-amber-900 sticky top-0">
                        <tr>
                          <th className="w-8 text-center py-1.5"></th>
                          <th className="text-left px-2 py-1.5">Row</th>
                          <th className="text-left px-2 py-1.5">Name</th>
                          <th className="text-left px-2 py-1.5">Email</th>
                          <th className="text-left px-2 py-1.5">Phone</th>
                          <th className="text-left px-2 py-1.5">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dupRows.map((d) => {
                          const checked = pickedRows.has(d.row);
                          const forced = !!d.forced_imported;
                          return (
                            <tr
                              key={`dup-${d.row}`}
                              className={forced ? "bg-emerald-50/60" : "hover:bg-amber-50/40"}
                              data-testid={`import-csv-dup-row-${d.row}`}
                            >
                              <td className="text-center">
                                {forced ? (
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline" data-testid={`import-csv-dup-forced-${d.row}`} />
                                ) : (
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={() => toggleRow(d.row)}
                                    disabled={forcing}
                                    data-testid={`import-csv-dup-check-${d.row}`}
                                  />
                                )}
                              </td>
                              <td className="px-2 py-1 text-muted-foreground tabular-nums">{d.row}</td>
                              <td className="px-2 py-1 text-foreground">
                                {(d.first_name || "")} {(d.last_name || "")}
                              </td>
                              <td className="px-2 py-1 text-muted-foreground truncate max-w-[180px]">{d.email || "—"}</td>
                              <td className="px-2 py-1 text-muted-foreground truncate max-w-[120px]">{d.phone || "—"}</td>
                              <td className="px-2 py-1 text-amber-800 truncate max-w-[220px]" title={d.reason}>
                                {forced ? "Imported as new (manual merge pending)" : d.reason}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <span className="text-[11px] text-amber-900" data-testid="import-csv-picked-count">
                      {pickedRows.size.toLocaleString()} selected
                    </span>
                    <Button
                      size="sm"
                      onClick={forceImportSelected}
                      disabled={pickedRows.size === 0 || forcing}
                      className="h-7 gap-1 text-xs"
                      data-testid="import-csv-force-import"
                    >
                      {forcing ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitMerge className="w-3 h-3" />}
                      Import selected anyway
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {phase === "failed" && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900 flex items-start gap-2" data-testid="import-csv-failed">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{job?.header_error || headerError || "Import failed."}</span>
            </div>
          )}
        </div>

        <DialogFooter className="flex sm:justify-between mt-3">
          <Button variant="link" size="sm" onClick={downloadSample} className="text-xs" data-testid="import-csv-sample-link">
            Download sample CSV
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={closeAndReset} disabled={phase === "uploading"} data-testid="import-csv-cancel">
              {phase === "done" || phase === "failed" ? "Close" : "Cancel"}
            </Button>
            {phase !== "done" && phase !== "processing" && (
              <Button
                onClick={doImport}
                disabled={!file || phase === "uploading"}
                data-testid="import-csv-start"
              >
                {phase === "uploading" ? "Uploading…" : "Start import"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
